"use client";

import { useSyncExternalStore } from "react";

/**
 * Consent for the analytics cookies this site sets (GA4, Meta Pixel). No cookie is set
 * until a visitor opts in (GA4 runs cookieless until then, the Pixel does not load), and
 * the choice is remembered locally, so this is the mechanism behind both the banner and
 * the "Cookies" link in the footer.
 *
 * The Google booking embed only loads when a visitor opens it, and Google manages its own
 * cookies inside that frame, so it is deliberately not gated here.
 */
export type Consent = "granted" | "denied";

/** Also read by the GA4 init script in components/analytics/Analytics.tsx. */
export const CONSENT_KEY = "jrats.consent";
const CONSENT_EVENT = "jrats:consent";

/** null means undecided, which is what shows the banner. */
const read = (): Consent | null => {
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null; // private window or storage blocked: treat as undecided, never as consent
  }
};

const announce = () => window.dispatchEvent(new Event(CONSENT_EVENT));

export const setConsent = (value: Consent) => {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* choice cannot be remembered; it still holds for this page view */
  }
  announce();
};

/** Forgets the choice so the banner comes back and it can be made again. */
export const resetConsent = () => {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* nothing stored to clear */
  }
  announce();
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CONSENT_EVENT, onChange);
  window.addEventListener("storage", onChange); // a decision made in another tab
  return () => {
    window.removeEventListener(CONSENT_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
};

const getServerSnapshot = () => null;

export const useConsent = () => useSyncExternalStore(subscribe, read, getServerSnapshot);
